/**
 * Standard-side decoration location defaults — the fallback used whenever a side hasn't been
 * (or hasn't yet been re-saved with) its own explicit Decoration Location. Mirrors the Impress
 * LOC codes seeded into ImpressDecorationLocationCodes (FF/FB/SL/SR) and the equivalent backend
 * fallback in MerchLoop-API (AutoMapperProfile.cs's DefaultInkSoftLocationNamesByAlias /
 * GetCustomizedProductDetailsHandler.cs's InkSoftLocationName ternary chain). Single source of
 * truth on the frontend — anywhere that used to hardcode this mapping should import it from here
 * instead of redeclaring it.
 */
export interface DecorationLocationDefault {
  code: string;
  name: string;
}

export const STANDARD_DECORATION_LOCATION_DEFAULTS: { [alias: string]: DecorationLocationDefault } = {
  'front': { code: 'FF', name: 'Full Front' },
  'back': { code: 'FB', name: 'Full Back' },
  'left sleeve': { code: 'SL', name: 'Shoulder Left' },
  'left_sleeve': { code: 'SL', name: 'Shoulder Left' },
  'sleeve_left': { code: 'SL', name: 'Shoulder Left' },
  'sleeveleft': { code: 'SL', name: 'Shoulder Left' },
  'left': { code: 'SL', name: 'Shoulder Left' },
  'right sleeve': { code: 'SR', name: 'Shoulder Right' },
  'right_sleeve': { code: 'SR', name: 'Shoulder Right' },
  'sleeve_right': { code: 'SR', name: 'Shoulder Right' },
  'sleeveright': { code: 'SR', name: 'Shoulder Right' },
  'right': { code: 'SR', name: 'Shoulder Right' }
};

function normalizeSideAlias(sideName: string): string {
  return (sideName || '').trim().toLowerCase();
}

/** e.g. "sleeveleft" / "Left Sleeve" → "Shoulder Left". Returns null for a non-standard side. */
export function getDefaultDecorationLocationName(sideName: string): string | null {
  const match = STANDARD_DECORATION_LOCATION_DEFAULTS[normalizeSideAlias(sideName)];
  return match ? match.name : null;
}

/** e.g. "sleeveleft" / "Left Sleeve" → "SL". Returns null for a non-standard side. */
export function getDefaultDecorationLocationCode(sideName: string): string | null {
  const match = STANDARD_DECORATION_LOCATION_DEFAULTS[normalizeSideAlias(sideName)];
  return match ? match.code : null;
}

// Reverse of the map above — decoration location NAME back to the canonical technical side
// alias. Needed because CustomizedProduct.SideName / ProductStyleAlternateDesign.SideName are now
// saved as the decoration name (e.g. "Full Front") instead of the technical identifier
// ("front") — see SaveHuddleProductStyles payload construction in
// product-color-selection.component.ts. Anything that still needs the technical identifier
// (matching a product's Sides-table Name/DisplayName, ImageFilePath_* field selection, internal
// map keys, etc.) must reverse-resolve through this first.
const DECORATION_LOCATION_NAME_TO_ALIAS: { [name: string]: string } = {
  'full front': 'front',
  'full back': 'back',
  'shoulder left': 'sleeveleft',
  'shoulder right': 'sleeveright'
};

/**
 * Resolve a stored SideName value back to the canonical technical alias used throughout the rest
 * of the frontend. Handles all shapes SideName can currently be in:
 *  - Legacy/not-yet-resaved rows: already a technical identifier ("front", "sleeveleft") — passed
 *    through as-is (just normalized).
 *  - Newly-saved rows, standard side: one of the 4 built-in decoration location defaults
 *    ("Full Front", "Shoulder Left") — reverse-mapped via the static table above.
 *  - Newly-saved rows, custom decoration location ("Left Chest", "Mid Chest", ...): not in the
 *    static table (there are ~40 possible Impress LOC codes, not just the 4 defaults). When the
 *    caller passes `currentSides` (the product's live Sides, each carrying its current
 *    InkSoftLocationName), look up which side is CURRENTLY configured with this label and return
 *    that side's technical alias — correct even if the label isn't one of the 4 defaults.
 * Always returns a usable (lowercased) string, never null — falls back to the normalized input
 * when nothing recognizes it (no currentSides supplied, or the side's config has since changed).
 */
export function resolveSideAlias(storedSideName: string, currentSides?: any[]): string {
  const normalized = normalizeSideAlias(storedSideName);
  if (DECORATION_LOCATION_NAME_TO_ALIAS[normalized]) {
    return DECORATION_LOCATION_NAME_TO_ALIAS[normalized];
  }
  if (currentSides && currentSides.length) {
    const match = currentSides.find((s: any) =>
      (s.InkSoftLocationName || '').trim().toLowerCase() === normalized
    );
    if (match) {
      return normalizeSideAlias(match.SideName || match.Name || match.DisplayName || normalized);
    }
  }
  return normalized;
}

/**
 * The side an existing design is opened on in the edit flow (product-color-selection's
 * `currentSideName`, which the Imprint Locations module shows as its locked base row).
 * Front when the design has a front side (or no saved side at all, as before); otherwise the
 * first saved side. A design saved only on a sleeve or the back used to open on a "Front" row
 * with no artwork behind it (Lift King A555, 2026-10-09).
 */
export function pickEditBaseSide(savedSideNames: string[]): string {
  const sides = (savedSideNames || [])
    .map(name => normalizeSideAlias(name || ''))
    .filter(name => !!name);
  if (sides.length === 0 || sides.some(name => resolveSideAlias(name) === 'front')) {
    return 'front';
  }
  // Only a recognised back/sleeve panel replaces front; an unresolved custom label keeps the old default.
  const panel = sides.map(name => resolveSideAlias(name)).find(name => EDIT_BASE_PANELS.indexOf(name) >= 0);
  return panel || 'front';
}

const EDIT_BASE_PANELS = ['back', 'sleeveleft', 'sleeveright'];
